import { ReactNode, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { FormField, LeadFormValues } from '../lib/forms';
import {
  MagicLocalAttachment,
  MagicServerAttachment,
  VoiceMagicTextarea,
} from './VoiceMagicTextarea';
import { AssigneePicker } from './AssigneePicker';

interface Props {
  fields: FormField[];
  values: LeadFormValues;
  onChange: (id: string, value: unknown) => void;
  /** Sprint 18 (S18.4) · weitergegeben an VoiceMagicTextarea. */
  onMagicAccepted?: () => void;
  /** Sprint 19 (S19.2/S19.3) · Anhänge unter Notiz-Textareas. */
  localAttachments?: MagicLocalAttachment[];
  serverAttachments?: MagicServerAttachment[];
  onAddLocalAttachment?: (file: File) => void;
  onRemoveLocalAttachment?: (idx: number) => void;
  /** Sprint 22 (S22.2) · IDs der Felder, die per OCR vorausgefüllt wurden — zeigt Sparkles-Indicator. */
  ocrAppliedFields?: string[];
}

export function FormRenderer({
  fields,
  values,
  onChange,
  onMagicAccepted,
  localAttachments,
  serverAttachments,
  onAddLocalAttachment,
  onRemoveLocalAttachment,
  ocrAppliedFields,
}: Props) {
  return (
    <div className="form-renderer">
      {fields.map((field) => (
        <FormFieldEl
          key={field.id}
          field={field}
          value={values[field.id]}
          onChange={onChange}
          onMagicAccepted={onMagicAccepted}
          localAttachments={localAttachments}
          serverAttachments={serverAttachments}
          onAddLocalAttachment={onAddLocalAttachment}
          onRemoveLocalAttachment={onRemoveLocalAttachment}
          ocrApplied={ocrAppliedFields?.includes(field.id) ?? false}
        />
      ))}
    </div>
  );
}

function FieldLabel({
  field,
  htmlFor,
  ocrApplied,
  children,
}: {
  field: FormField;
  htmlFor?: string;
  ocrApplied?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="form-field__label-row">
      <label className="form-field__label" htmlFor={htmlFor}>
        {field.label.toUpperCase()}
        {field.required ? ' *' : ''}
      </label>
      {field.help ? <HelpTooltip text={field.help} /> : null}
      {/* Sprint 22 (S22.2) · OCR-Indicator pro Feld */}
      {ocrApplied ? (
        <span className="form-field__ocr-indicator" title="Per Visitenkarten-OCR vorausgefüllt">
          <Sparkles size={11} aria-hidden="true" />
          <span>OCR</span>
        </span>
      ) : null}
      {children}
    </div>
  );
}

function HelpTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="help-tooltip">
      <button
        type="button"
        className="help-tooltip__btn"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        aria-label="Hilfe"
        title={text}
      >
        ?
      </button>
      {open ? <span className="help-tooltip__bubble">{text}</span> : null}
    </span>
  );
}

function FormFieldEl({
  field,
  value,
  onChange,
  onMagicAccepted,
  localAttachments,
  serverAttachments,
  onAddLocalAttachment,
  onRemoveLocalAttachment,
  ocrApplied,
}: {
  field: FormField;
  value: unknown;
  onChange: (id: string, value: unknown) => void;
  onMagicAccepted?: () => void;
  localAttachments?: MagicLocalAttachment[];
  serverAttachments?: MagicServerAttachment[];
  onAddLocalAttachment?: (file: File) => void;
  onRemoveLocalAttachment?: (idx: number) => void;
  ocrApplied?: boolean;
}) {
  const change = (v: unknown) => onChange(field.id, v);
  const inputId = `f-${field.id}`;

  if (field.type === 'image-upload') {
    return null;
  }

  if (field.type === 'assignee') {
    // Sprint 13 (S13.1) · Searchable combobox — User tippt Name → typeahead.
    // Speichert UUID-String (kompatibel zu lead.uebernehmender_id).
    return (
      <div className="form-field">
        <FieldLabel field={field} htmlFor={inputId} ocrApplied={ocrApplied} />
        <AssigneePicker
          inputId={inputId}
          value={typeof value === 'string' ? value : ''}
          onChange={(id) => change(id)}
          placeholder="Name suchen…"
        />
      </div>
    );
  }

  if (field.type === 'select') {
    const options = field.options ?? [];
    const useBubbles = options.length <= 7 && options.every((o) => o.label.length <= 25);
    const current = typeof value === 'string' ? value : '';
    if (useBubbles) {
      return (
        <div className="form-field">
          <FieldLabel field={field} ocrApplied={ocrApplied} />
          <div className="bubble-group">
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`bubble ${current === opt.value ? 'bubble--active' : ''}`}
                onClick={() => change(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="form-field">
        <FieldLabel field={field} htmlFor={inputId} ocrApplied={ocrApplied} />
        <select
          id={inputId}
          className="auth-input form-field__select"
          required={field.required}
          value={current}
          onChange={(e) => change(e.target.value)}
        >
          <option value="">— bitte wählen —</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === 'multiselect') {
    const arrValue = Array.isArray(value) ? (value as string[]) : [];
    const toggle = (v: string) => {
      const next = arrValue.includes(v) ? arrValue.filter((x) => x !== v) : [...arrValue, v];
      change(next);
    };
    return (
      <fieldset className="form-field">
        <FieldLabel field={field} ocrApplied={ocrApplied} />
        <div className="bubble-group">
          {(field.options ?? []).map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`bubble ${arrValue.includes(opt.value) ? 'bubble--active' : ''}`}
              onClick={() => toggle(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </fieldset>
    );
  }

  if (field.type === 'textarea') {
    if (field.voice || field.magic) {
      return (
        <VoiceMagicTextarea
          field={field}
          value={value}
          onChange={(v) => change(v)}
          onMagicAccepted={onMagicAccepted}
          localAttachments={localAttachments}
          serverAttachments={serverAttachments}
          onAddLocalAttachment={onAddLocalAttachment}
          onRemoveLocalAttachment={onRemoveLocalAttachment}
        />
      );
    }
    return (
      <div className="form-field">
        <FieldLabel field={field} htmlFor={inputId} ocrApplied={ocrApplied} />
        <textarea
          id={inputId}
          className="auth-input form-field__textarea"
          required={field.required}
          rows={field.rows ?? 4}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => change(e.target.value)}
        />
      </div>
    );
  }

  if (field.type === 'rating') {
    const min = field.min ?? 1;
    const max = field.max ?? 10;
    const num = typeof value === 'number' ? value : null;
    return (
      <div className="form-field">
        <FieldLabel field={field} htmlFor={inputId} ocrApplied={ocrApplied}>
          <span className="form-field__hint" style={{ marginLeft: 8 }}>
            ({min}–{max})
          </span>
        </FieldLabel>
        <div className="rating-stars">
          {Array.from({ length: max - min + 1 }, (_, i) => min + i).map((n) => (
            <button
              key={n}
              type="button"
              className={`rating-star ${num !== null && num >= n ? 'rating-star--active' : ''}`}
              onClick={() => change(n === num ? null : n)}
              aria-label={`Bewertung ${n}/${max}`}
            >
              ★
            </button>
          ))}
          {num !== null ? (
            <span className="rating-stars__value">{num}/{max}</span>
          ) : null}
        </div>
      </div>
    );
  }

  if (field.type === 'boolean') {
    return (
      <label className="form-field__check form-field--inline">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => change(e.target.checked)}
        />
        <span className="form-field__label">{field.label}</span>
        {field.help ? <HelpTooltip text={field.help} /> : null}
      </label>
    );
  }

  return (
    <div className="form-field">
      <FieldLabel field={field} htmlFor={inputId} ocrApplied={ocrApplied} />
      <input
        id={inputId}
        className="auth-input"
        type={field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'text'}
        required={field.required}
        autoComplete={field.type === "email" ? "email" : field.type === "phone" ? "tel" : "off"}
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => change(e.target.value)}
      />
    </div>
  );
}
